import React, { useEffect, useState } from "react";
import { Loader2, Link2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { venuesApi } from "../../utils/api";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { displayLabel } from "../../utils/helpers";

const DISAGREE = "Rooms disagree — left empty";

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd>{value ?? <span className="italic text-muted-foreground">{DISAGREE}</span>}</dd>
    </div>
  );
}

function Preview({ preview }) {
  const coords =
    preview.latitude != null && preview.longitude != null ? `${preview.latitude}, ${preview.longitude}` : null;
  return (
    <dl className="grid gap-2 rounded-lg border border-border p-3 text-sm sm:grid-cols-2">
      <Field label="Name" value={preview.name} />
      <Field label="Address" value={preview.address} />
      <Field label="Coordinates" value={coords} />
    </dl>
  );
}

// Link two venues as rooms of one place. Previews first (dry run); the API asks for the roles
// (`needs_choice`) when neither venue is already a main.
export default function LinkRoomsDialog({ open, venueA, venueB, onClose, onLinked }) {
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Document ids: room links are stored in that space, which a legacy venue's `_source.id` is not.
  const idA = venueA?._id || venueA?.id;
  const idB = venueB?._id || venueB?.id;
  const nameA = displayLabel(venueA?.preferred_name, venueA?.name);
  const nameB = displayLabel(venueB?.preferred_name, venueB?.name);

  useEffect(() => {
    if (open) setChoice(null);
  }, [open, idA, idB]);

  useEffect(() => {
    if (!open || !idA || !idB) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    venuesApi
      .link(idA, idB, { choice, dryRun: true })
      .then((data) => !cancelled && setResult(data))
      .catch((e) => {
        if (cancelled) return;
        setResult(null);
        setError(e?.response?.data?.detail || "Could not preview the link");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, idA, idB, choice]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await venuesApi.link(idA, idB, { choice });
      // The write re-plans from the venues as stored: a group changed since the preview can make it
      // ask for the roles (200, success: false) instead of linking. Show that, never "linked".
      if (data?.success === false) {
        setResult(data);
        setError(data.message || "The venues changed since the preview");
        return;
      }
      await onLinked?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not link the venues");
    } finally {
      setBusy(false);
    }
  };

  const needsChoice = !!result?.needs_choice;
  const options = [
    ["both_rooms", "Both are rooms — create a main venue"],
    ["a_main", `${nameA} is the main venue`],
    ["b_main", `${nameB} is the main venue`],
  ];
  const mainName = result?.main_preview?.name || "the main venue";
  // A dry run that would create the main has no id for it yet.
  const created = !!result?.generated && result?.main_id == null;
  // The main can be a third venue (one of the two is already its room), so name the new rooms
  // from the API's answer rather than inferring them from the main's id.
  const newRooms = (result?.new_room_ids || []).map((id) => (id === idA ? nameA : id === idB ? nameB : id));
  const canConfirm = !!result && result.success !== false && !needsChoice && !loading && !busy;

  return (
    <Modal open={open} onOpenChange={(o) => { if (!busy && !o) onClose?.(); }}>
      <ModalContent
        title="Rooms of the same venue"
        description={`${nameA} and ${nameB} are separate spaces of one place.`}
      >
        <div className="space-y-4 p-4 sm:p-6">
          <ul className="space-y-1 text-sm">
            {[venueA, venueB].map((venue, i) => (
              <li key={i}>
                <p className="font-medium">{i === 0 ? nameA : nameB}</p>
                <ProviderLabel preferred={venue?.preferred_name} original={venue?.name} />
              </li>
            ))}
          </ul>
          {(needsChoice || choice) && (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">Which is the main venue?</legend>
              {options.map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="room-choice"
                    checked={choice === value}
                    onChange={() => setChoice(value)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          )}

          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}

          {!loading && result && !needsChoice && result.success !== false && (
            created ? (
              <div className="space-y-2">
                <p className="text-sm">A main venue is created from the rooms:</p>
                <Preview preview={result.main_preview} />
              </div>
            ) : newRooms.length === 0 ? (
              <p className="text-sm">Both are already rooms of {mainName}; nothing changes.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm">
                  {newRooms.join(" and ")} becomes a room of {mainName}.
                </p>
                {result.generated && result.main_preview && (
                  <>
                    <p className="text-sm">Its main venue is recomputed from its rooms:</p>
                    <Preview preview={result.main_preview} />
                  </>
                )}
              </div>
            )
          )}

          {!loading && result?.success === false && !needsChoice && result.message && (
            <p className="text-sm text-destructive">{result.message}</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <ModalFooter className="px-4 pb-4 sm:px-6 sm:pb-6">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
            Link as rooms
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
